import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  CustomFontFamily,
  defaultBrandKit,
} from "@redotech/redo-content-builder-common/brand-kit";
import { getCustomFontFamily } from "@redotech/redo-content-builder-common/brand-kit/font-utils";
import {
  DEFAULT_ORDER_LOOKUP_SETTINGS,
  OrderLookupSettings,
} from "@redotech/redo-order-tracking-lookup-common/order-lookup-defaults";
import { EmbeddedOrderTrackingLogin } from "@redotech/redo-web/embedded-order-tracking-login/embedded-order-tracking-login";
import { FontStyleLoader } from "@redotech/redo-web/form-builder/inspectors/custom-fonts/hooks/font-style-loader";
import { getTrackingPageForStorefront } from "@redotech/shopify-sdk/rpc/schema/get-tracking-page-for-storefront";
import { singleOrderAuth } from "@redotech/shopify-sdk/rpc/schema/single-order-auth";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { getAutocheckCache } from "../autocheck-cache";
import { RedoShopifyRpcClientContext } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { buildLoadingBgParam } from "./build-loading-bg-param";
import { resolveOrderLookupConfig } from "./resolve-order-lookup-config";

export const OrderTrackingLogin = memo(function OrderTrackingLogin({
  returnAppUrl,
}: {
  returnAppUrl: string | undefined;
}) {
  const rpcClient = useRequiredContext(RedoShopifyRpcClientContext);

  // In the Shopify theme editor, this runs inside a sandboxed iframe whose
  // origin is the store's public myshopify.com domain. Chrome's Private Network
  // Access policy blocks requests from public origins to loopback addresses
  // (*.getredo.localhost → 127.0.0.1), so RPC calls fail in local dev. This
  // does not affect production since the API is at a public URL. When the RPC
  // fails, we fall back to defaults so the form still renders as a preview.
  const fetchedSettings = useLoad(async () => {
    return await resolveOrderLookupConfig({
      getAutocheck: getAutocheckCache,
      fetchViaRpc: () => rpcClient(getTrackingPageForStorefront, {}),
      onFallback: (error) => {
        if (error) {
          log("OT-login: autocheck unavailable, falling back to RPC", error);
        }
      },
    });
  }, [rpcClient]);

  const settings =
    fetchedSettings.value?.orderLookupSettings ?? DEFAULT_ORDER_LOOKUP_SETTINGS;
  const resolvedBrandKit = fetchedSettings.value?.brandKit ?? defaultBrandKit;
  const resolvedWidgetSlug = fetchedSettings.value?.widgetSlug;
  const resolvedLoadingBg = fetchedSettings.value?.loadingBackgroundColor;

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [iframeSrc, setIframeSrc] = useState<string | undefined>();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const buildIframeSrc = useCallback(
    (token: string) => {
      const resolvedSlug = resolvedWidgetSlug ?? globalThis.redoWidgetId;
      if (!returnAppUrl || !resolvedSlug) {
        return undefined;
      }
      const pageParams = new URLSearchParams(window.location.search);
      const shipmentId = pageParams.get("shipment_id");
      const fulfillmentIndex = pageParams.get("fulfillment_index");
      const baseUrl = `${returnAppUrl}/widget_id/${resolvedSlug}`;
      // A shipment id selects the package; the index path is only for legacy links.
      const path =
        shipmentId === null && fulfillmentIndex !== null
          ? `embedded-tracking/${fulfillmentIndex}`
          : "embedded-tracking";
      const shipmentParam =
        shipmentId === null
          ? ""
          : `&shipment_id=${encodeURIComponent(shipmentId)}`;
      return `${baseUrl}/returns-portal/orders/${path}?token=${encodeURIComponent(token)}${shipmentParam}${buildLoadingBgParam(resolvedLoadingBg)}`;
    },
    [resolvedWidgetSlug, returnAppUrl, resolvedLoadingBg],
  );

  // Bypass login form if token is already in the URL
  useEffect(() => {
    const pageParams = new URLSearchParams(window.location.search);
    const existingToken = pageParams.get("token");
    if (existingToken) {
      const src = buildIframeSrc(existingToken);
      if (src) {
        setPending(true);
        setIframeSrc(src);
      }
    }
  }, [buildIframeSrc]);

  const handleSubmit = useCallback(
    async (emailOrZip: string, orderIdTrackingNumber: string) => {
      setPending(true);
      setError(undefined);
      setIframeLoaded(false);
      setIframeSrc(undefined);
      try {
        const { customerAuthToken } = await rpcClient(singleOrderAuth, {
          emailOrZip,
          orderIdTrackingNumber,
        });

        const src = buildIframeSrc(customerAuthToken);
        if (!src) {
          setError("Unable to load order. Please refresh and try again.");
          setPending(false);
          return;
        }

        setIframeSrc(src);
      } catch (e: any) {
        log("Single order auth error:", e);
        setError(
          e?.message || "Unable to find order. Please check your credentials.",
        );
        setPending(false);
      }
    },
    [rpcClient, buildIframeSrc],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "redo:frame-height" && iframeRef.current) {
        iframeRef.current.style.height = `${event.data.height}px`;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Don't render until settings are loaded to avoid style flicker
  if (fetchedSettings.pending) {
    return null;
  }

  return (
    <>
      <OrderLookupFontLoader
        customFontFamilies={resolvedBrandKit.customFontFamilies}
        settings={settings}
      />
      {/* Keep showing the login form until iframe loads to avoid flash of blank content */}
      {!iframeLoaded && (
        <EmbeddedOrderTrackingLogin
          brandKit={resolvedBrandKit}
          error={error}
          onSubmit={handleSubmit}
          pending={pending || !!iframeSrc}
          settings={settings}
        />
      )}

      {iframeSrc && (
        <iframe
          onLoad={() => {
            setIframeLoaded(true);
            setPending(false);
          }}
          ref={iframeRef}
          src={iframeSrc}
          style={{
            width: "100%",
            border: "none",
            display: iframeLoaded ? "block" : "none",
          }}
          title="Order tracking"
        />
      )}
    </>
  );
});

const OrderLookupFontLoader = memo(function OrderLookupFontLoader({
  settings,
  customFontFamilies,
}: {
  settings: OrderLookupSettings;
  customFontFamilies: CustomFontFamily[] | null | undefined;
}) {
  const titleFont = settings.titleFontFamily
    ? getCustomFontFamily(settings.titleFontFamily, customFontFamilies)
    : undefined;
  const bodyFont = settings.bodyFontFamily
    ? getCustomFontFamily(settings.bodyFontFamily, customFontFamilies)
    : undefined;

  return (
    <>
      {titleFont?.styles.map((style) => (
        <FontStyleLoader
          fontFamily={titleFont.fontFamily}
          key={`title-${style._id}`}
          style={style}
        />
      ))}
      {bodyFont?.styles.map((style) => (
        <FontStyleLoader
          fontFamily={bodyFont.fontFamily}
          key={`body-${style._id}`}
          style={style}
        />
      ))}
    </>
  );
});
